import type { Translate } from "@similarweb/i18n-provider";

import type { UntaggedFilterValue } from "app/pages/digital-marketing/keywords-campaigns/reports/types";

import { UNTAGGED_ID } from "./constants";
import type { IncludeExcludeTagsState } from "./types";

/**
 * Returns true when a dismiss event originated inside a top-layer overlay
 * (popover content or nested combobox portal).
 */
export const shouldIgnorePopoverDismiss = (event?: Event): boolean => {
    const target = event?.target;
    return target instanceof Element && Boolean(target.closest("[data-react-aria-top-layer]"));
};

/**
 * Resolves the untaggedFilter value, handling backwards compatibility with withUntagged param
 */
export const resolveUntaggedFilter = (
    untaggedFilter: UntaggedFilterValue | undefined,
    withUntagged: string | undefined,
): UntaggedFilterValue | undefined => {
    if (untaggedFilter) {
        return untaggedFilter;
    }
    if (withUntagged === "true") {
        return "include";
    }
    return undefined;
};

/**
 * Returns tags array with UNTAGGED_ID prepended if the filter matches
 */
export const getTagsWithUntagged = (
    tags: string[],
    untaggedFilter: UntaggedFilterValue | undefined,
    filterType: UntaggedFilterValue,
): string[] => {
    if (untaggedFilter === filterType) {
        return [UNTAGGED_ID, ...tags];
    }
    return tags;
};

/**
 * Parses comma-separated tags parameter string into array
 */
export const parseTagsParam = (tagsParam: string | undefined): string[] => {
    if (!tagsParam) {
        return [];
    }
    return tagsParam.split(",").filter(Boolean);
};

/**
 * Returns text for applied tags filter
 */
export const getAppliedTagsText = (appliedState: IncludeExcludeTagsState, translate: Translate): string | null => {
    const appliedIncludeTags = getTagsWithUntagged(appliedState.includeTags, appliedState.untaggedFilter, "include");
    const appliedExcludeTags = getTagsWithUntagged(appliedState.excludeTags, appliedState.untaggedFilter, "exclude");

    const includeCount = appliedIncludeTags.length;
    const excludeCount = appliedExcludeTags.length;
    const total = includeCount + excludeCount;

    if (total === 0) {
        return null;
    }

    if (includeCount === 1 && excludeCount === 0) {
        const tagName =
            appliedIncludeTags[0] === UNTAGGED_ID
                ? translate("keywords.report.tags.filter.tiers.untagged")
                : appliedIncludeTags[0];
        return translate("RT.reports.tags.filter.including.single", { name: tagName });
    }

    if (excludeCount === 1 && includeCount === 0) {
        const tagName =
            appliedExcludeTags[0] === UNTAGGED_ID
                ? translate("keywords.report.tags.filter.tiers.untagged")
                : appliedExcludeTags[0];
        return translate("RT.reports.tags.filter.excluding.single", { name: tagName });
    }

    return translate("RT.reports.tags.filter.multiple", { count: total });
};
